import React, { useContext, useEffect, useState, useRef } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Box } from '@atlaskit/primitives';

import { useUntruncatedDiffCounts } from 'src/components/diff-set/hooks/untruncated-diff-counts';
import { PlaceholderDiffList } from 'src/components/diff-set/placeholder-diff-list';
import { SingleFileDiffList } from 'src/components/diff-set/single-file-diff-list';
import { useIntl } from 'src/hooks/intl';
import {
  INITIAL_DIFFS_RENDERED,
  publishPullRequestTrackEvent,
} from 'src/redux/pull-request/actions';
import DiffCommentFilters from 'src/sections/repository/sections/pull-request/components/diff-comment-filters/diff-comment-filters';
import { FilterAndSortSettings } from 'src/sections/repository/sections/pull-request/components/diff-comment-filters/styles';
import { DiffCommentNavigator } from 'src/sections/repository/sections/pull-request/components/diff-comment-navigator/diff-comment-navigator';
import { DiffFeaturesContext } from 'src/sections/repository/sections/pull-request/components/diff-features-context';
import * as styles from 'src/sections/repository/sections/pull-request/components/diff-set.styled';
import { DiffSort } from 'src/sections/repository/sections/pull-request/components/diff-sort';
import messages from 'src/sections/repository/sections/pull-request/components/diff.i18n';
import { HeaderVisibilityCheck } from 'src/sections/repository/sections/pull-request/components/header-visibility-check';
import { NoFilesMatchFiltersMessage } from 'src/sections/repository/sections/pull-request/components/no-files-match-filter-message';
import { ReviewedFilesProgressBar } from 'src/sections/repository/sections/pull-request/components/reviewed-files-progress-bar/reviewed-files-progress-bar';
import { SingleFileNavigation } from 'src/sections/repository/sections/pull-request/components/single-file-mode/single-file-navigation';
import { useCommentsFilterDetails as useCommentsFilterDetailsDI } from 'src/sections/repository/sections/pull-request/hooks/comment-filter-details';
import { useIsSingleFileModeActive as useIsSingleFileModeActiveDI } from 'src/sections/repository/sections/pull-request/hooks/is-single-file-mode';
import { Diff } from 'src/types/pull-request';

import { SortAndReviewed } from '../../sections/repository/sections/pull-request/components/reviewed-files-progress-bar/styles';

import { StickyDiffHeader } from './diff-set.style';
import { useSettingsHeaderVisibilityChanged } from './hooks/diff-set-details';

export type DiffSetProps = {
  files: Diff[];
  diffMetadataProps: DiffMetadataProps;
  useIsSingleFileModeActive?: typeof useIsSingleFileModeActiveDI;
  useCommentsFilterDetails?: typeof useCommentsFilterDetailsDI;
  tabbedView?: boolean;
};

export type TwoHashDiffMetadataProps = {
  sourceHash: string;
  destinationHash: string;
  sourceRepoFullSlug: string;
  destinationRepoFullSlug: string;
};

export type OneHashDiffMetadataProps = {
  sourceHash: string;
  destinationHash?: never;
  sourceRepoFullSlug: string;
  destinationRepoFullSlug?: never;
};

export type CombinedDiffMetadataProps = {
  sourceHash: string;
  destinationHash?: string;
  sourceRepoFullSlug: string;
  destinationRepoFullSlug?: string;
};

export type DiffMetadataProps =
  | CombinedDiffMetadataProps
  | TwoHashDiffMetadataProps
  | OneHashDiffMetadataProps;

export const DiffSet = ({
  files,
  diffMetadataProps,
  useIsSingleFileModeActive = useIsSingleFileModeActiveDI,
  useCommentsFilterDetails = useCommentsFilterDetailsDI,
  tabbedView = false,
}: DiffSetProps) => {
  const intl = useIntl();

  const { hasComments, hasCommentNav } = useContext(DiffFeaturesContext);

  const { isSingleFileModeActive, isGlobalSingleFileModeEnabled } =
    useIsSingleFileModeActive();

  const { untruncatedPullRequestLinesChanged, untruncatedDiffFileCount } =
    useUntruncatedDiffCounts();
  const pullRequestFileMode = isSingleFileModeActive
    ? 'singleFileMode'
    : 'allFilesMode';

  // COREX-6510 Remove useCommentsFilterDetails and related code that is only used for this filter
  const { isCommentsFilterActive } = useCommentsFilterDetails();
  const isCommentsFilterActiveAndEnabled =
    isCommentsFilterActive && hasComments;

  const { hasMarkFilesAsViewed } = useContext(DiffFeaturesContext);

  const dispatchSettingsHeaderVisibilityChanged =
    useSettingsHeaderVisibilityChanged();

  const dispatch = useDispatch();

  const [isCommentNavOpen, setIsCommentNavOpen] = useState(false);
  const [isDiffSortOpen, setIsDiffSortOpen] = useState(false);

  const diffHeaderRef = useRef(null);

  // action kickstarts URL permalink scrolling sagas (scrollToLine, scrollToFilepath)
  useEffect(() => {
    dispatch({ type: INITIAL_DIFFS_RENDERED });
    dispatch(
      publishPullRequestTrackEvent({
        action: 'rendered',
        actionSubject: 'pullRequestDiff',
        actionSubjectId: '',
        attributes: {
          pullRequestFiles: untruncatedDiffFileCount,
          pullRequestLinesChanged: untruncatedPullRequestLinesChanged,
          pullRequestFilemode: pullRequestFileMode,
        },
      })
    );
  }, [
    dispatch,
    pullRequestFileMode,
    untruncatedDiffFileCount,
    untruncatedPullRequestLinesChanged,
  ]);

  const renderDiffHeader = () => {
    if (tabbedView) {
      return null;
    }
    if (hasComments && hasCommentNav) {
      return (
        <StickyDiffHeader
          ref={diffHeaderRef}
          isCommentNavOpen={isCommentNavOpen}
          isDiffSortOpen={isDiffSortOpen}
          isIterativeReviewSelectorOpen={false}
        >
          <FilterAndSortSettings>
            <DiffCommentNavigator
              isCommentNavOpen={isCommentNavOpen}
              setIsCommentNavOpen={setIsCommentNavOpen}
            />
            <SortAndReviewed>
              <DiffSort
                isDiffSortOpen={isDiffSortOpen}
                setIsDiffSortOpen={setIsDiffSortOpen}
              />
              {hasMarkFilesAsViewed && (
                <ReviewedFilesProgressBar
                  numTotalFiles={untruncatedDiffFileCount}
                />
              )}
            </SortAndReviewed>
          </FilterAndSortSettings>
        </StickyDiffHeader>
      );
    } else if (hasComments) {
      return (
        <FilterAndSortSettings>
          <DiffCommentFilters />
          <DiffSort
            isDiffSortOpen={isDiffSortOpen}
            setIsDiffSortOpen={setIsDiffSortOpen}
          />
        </FilterAndSortSettings>
      );
    }
    return <></>;
  };

  return (
    <Box role="region" aria-label={intl.formatMessage(messages.diffSetLabel)}>
      <HeaderVisibilityCheck
        dispatchSettingsHeaderVisibilityChanged={
          dispatchSettingsHeaderVisibilityChanged
        }
      />
      {!tabbedView && (
        <styles.SettingsHeader>
          <styles.GroupedContainer>
            <FormattedMessage
              tagName="h2"
              {...messages.fileCount}
              values={{ fileCount: untruncatedDiffFileCount }}
            />
          </styles.GroupedContainer>
          <styles.GroupedContainer>
            {/* // global vs isSFMENabled  */}
            {isSingleFileModeActive && <SingleFileNavigation />}
          </styles.GroupedContainer>
        </styles.SettingsHeader>
      )}
      {renderDiffHeader()}
      {isCommentsFilterActiveAndEnabled && !files.length && (
        <NoFilesMatchFiltersMessage />
      )}
      {/* // global vs isSFMENabled ?*/}
      {isSingleFileModeActive && files.length ? (
        <SingleFileDiffList
          file={files[0]}
          isGlobalSingleFileModeEnabled={isGlobalSingleFileModeEnabled}
          diffMetadataProps={diffMetadataProps}
        />
      ) : (
        <PlaceholderDiffList
          files={files}
          diffMetadataProps={diffMetadataProps}
        />
      )}
    </Box>
  );
};
